import React, { useState } from "react";

import useId from "@kikoff/hooks/src/useId";
import useStateOrBind from "@kikoff/hooks/src/useStateOrBind";
import { combineClasses } from "@kikoff/utils/src/string";

import styles from "./Switch.module.scss";

declare namespace Switch {
  interface Props extends React.HTMLProps<HTMLInputElement> {
    loading?: boolean;
  }
}

export default Switch;
function Switch({ loading = false, ...props }: Switch.Props) {
  const id = useId(props.id);

  const [checked, setChecked] = useStateOrBind<
    boolean,
    React.ChangeEvent<HTMLInputElement>
  >(props.defaultChecked, [
    props.checked,
    (_, e) => {
      props.onChange(e);
    },
  ]);

  return (
    <label
      className={combineClasses(
        styles.switch,
        checked && styles.checked,
        loading && styles.loading
      )}
      htmlFor={id}
    >
      <div className={styles.handle}>
        <div className={styles.spinner} />
      </div>
      <input
        type="checkbox"
        {...props}
        checked={checked}
        onChange={(e) => {
          setChecked(e.currentTarget.checked, e);
        }}
        id={id}
      />
    </label>
  );
}

declare namespace Switch.withLabel {
  interface Props extends Switch.Props {}
}

Switch.WithLabel = ({
  checked: _checked,
  defaultChecked = false,
  onChange: _onChange,
  className,
  ...props
}: Switch.withLabel.Props) => {
  const controlled = typeof _checked === "boolean";
  const id = useId(props.id);

  const [checkedInternal, setCheckedInternal] = useState(defaultChecked);

  const [checked, onChange] = controlled
    ? [_checked, _onChange]
    : [
        checkedInternal,
        (e) => {
          setCheckedInternal(e.currentTarget.checked);
          onChange?.(e);
        },
      ];

  return (
    <label className={combineClasses(styles["with-label"], className)} id={id}>
      <div className="text:regular color:moderate">
        {checked ? "ON" : "OFF"}
      </div>
      <Switch checked={checked} onChange={onChange} {...props} />
    </label>
  );
};
